"use client";

import { BgLayer } from "./bg/BgPrimitives";
import { motion } from "framer-motion";

export default function WhatYouGet() {
  const containerVariants = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.1 } },
  };
  const cardVariants = {
    hidden: { opacity: 0, y: 32 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] as [number, number, number, number] } },
  };
  const iconVariants = {
    hidden: { scale: 0.5, opacity: 0 },
    visible: { scale: 1, opacity: 1, transition: { type: "spring" as const, stiffness: 300, damping: 18, delay: 0.1 } },
  };

  return (
    <section id="benefits" className="bg-white py-20 px-4 relative overflow-hidden section-grain">
      <BgLayer />
      <div className="container mx-auto max-w-5xl relative z-10">
        <motion.div
          initial={{ opacity: 0, x: -24 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.55, ease: [0.25, 0.46, 0.45, 0.94] }}
        >
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            What&apos;s in it for you
          </h2>
          <p className="text-muted-foreground text-base md:text-lg leading-relaxed max-w-3xl">
            This program is built to help you benefit both now and later.{" "}
            <strong className="text-foreground">
              You might want to avoid overspending, understand the real cost of what you buy, make
              better use of your income, prepare for freelancing or work, or start thinking like
              someone who wants to grow money rather than just spend it.
            </strong>{" "}
            It is about living your present better and planning your future more intentionally.
          </p>
        </motion.div>

        <motion.div
          className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-10"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
        >
          <motion.div
            className="bg-muted border border-muted/60 shadow-sm rounded-xl p-6"
            variants={cardVariants}
            whileHover={{ y: -4, transition: { duration: 0.2 } }}
          >
            <motion.div variants={iconVariants}>
              <img src="/icons/JA Full-Color_Budget.png" alt="" width={104} height={104} className="mb-3" />
            </motion.div>
            <h3 className="text-lg font-semibold text-foreground mb-2">
              Handle everyday money better
            </h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Understand spending, saving, budgeting, and the choices that affect your financial
              stability.
            </p>
          </motion.div>

          <motion.div
            className="bg-muted border border-muted/60 shadow-sm rounded-xl p-6"
            variants={cardVariants}
            whileHover={{ y: -4, transition: { duration: 0.2 } }}
          >
            <motion.div variants={iconVariants}>
              <img src="/icons/JA Full-Color_GROWTH.png" alt="" width={104} height={104} className="mb-3" />
            </motion.div>
            <h3 className="text-lg font-semibold text-foreground mb-2">
              Think beyond the next purchase
            </h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Start seeing how money decisions connect to future opportunities, goals, and
              independence.
            </p>
          </motion.div>

          <motion.div
            className="bg-muted border border-muted/60 shadow-sm rounded-xl p-6"
            variants={cardVariants}
            whileHover={{ y: -4, transition: { duration: 0.2 } }}
          >
            <motion.div variants={iconVariants}>
              <img src="/icons/JA Full-Color_LEARNING.png" alt="" width={104} height={104} className="mb-3" />
            </motion.div>
            <h3 className="text-lg font-semibold text-foreground mb-2">
              Learn what school often does not teach clearly
            </h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              From taxes on what you buy to borrowing, saving, and investing, gain practical
              knowledge you can actually use.
            </p>
          </motion.div>

          <motion.div
            className="bg-muted border border-muted/60 shadow-sm rounded-xl p-6"
            variants={cardVariants}
            whileHover={{ y: -4, transition: { duration: 0.2 } }}
          >
            <motion.div variants={iconVariants}>
              <img src="/icons/JA Full-Color_WORK READINESS - CAREER EXPLORATION.png" alt="" width={104} height={104} className="mb-3" />
            </motion.div>
            <h3 className="text-lg font-semibold text-foreground mb-2">Prepare for real life</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Whether you become a young professional, freelancer, or entrepreneur, learn how to
              make the most of your money.
            </p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
